/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { AlertTriangle, X, FilePlus, FileEdit, Trash2, FileSymlink } from 'lucide-react'
import type { FileToolAction } from '@/providers/file-tools'

interface FileToolConfirmModalProps {
  action: FileToolAction
  onConfirm: () => void
  onCancel: () => void
}

export function FileToolConfirmModal({ action, onConfirm, onCancel }: FileToolConfirmModalProps) {
  const getIcon = () => {
    switch (action.type) {
      case 'create_file':
        return <FilePlus className="w-5 h-5 text-green-500" />
      case 'update_file':
        return <FileEdit className="w-5 h-5 text-blue-500" />
      case 'delete_file':
        return <Trash2 className="w-5 h-5 text-red-500" />
      case 'rename_file':
        return <FileSymlink className="w-5 h-5 text-yellow-500" />
    }
  }

  const getTitle = () => {
    switch (action.type) {
      case 'create_file':
        return 'Confirm File Creation'
      case 'update_file':
        return 'Confirm File Update'
      case 'delete_file':
        return 'Confirm File Deletion'
      case 'rename_file':
        return 'Confirm File Rename'
    }
  }

  const getWarningMessage = () => {
    switch (action.type) {
      case 'create_file':
        return 'The AI assistant wants to create a new file. Please review the content below before confirming.'
      case 'update_file':
        return 'The AI assistant wants to update an existing file. Please review the changes below before confirming.'
      case 'delete_file':
        return 'The AI assistant wants to delete a file. This action cannot be undone.'
      case 'rename_file':
        return 'The AI assistant wants to rename/move a file. Please review the details below before confirming.'
    }
  }

  const getButtonText = () => {
    switch (action.type) {
      case 'create_file':
        return 'Create File'
      case 'update_file':
        return 'Update File'
      case 'delete_file':
        return 'Delete File'
      case 'rename_file':
        return 'Rename File'
    }
  }

  const getButtonClass = () => {
    switch (action.type) {
      case 'delete_file':
        return 'bg-red-600 hover:bg-red-700 text-white'
      default:
        return 'btn-primary'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {getIcon()}
            <h2 className="text-lg font-title">{getTitle()}</h2>
          </div>
          <button onClick={onCancel} className="text-foreground-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning */}
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
          <p className="text-sm text-yellow-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {getWarningMessage()}
          </p>
        </div>

        {/* File Details */}
        <div className="flex-1 overflow-y-auto mb-4">
          {action.type === 'create_file' && (
            <>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">File Path:</div>
                <div className="p-2 bg-background-tertiary rounded border border-border">
                  <div className="text-sm font-mono">{action.path}</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">
                  Content ({action.content?.length || 0} characters):
                </div>
                <div className="p-3 bg-background-tertiary rounded border border-border max-h-96 overflow-y-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap">{action.content}</pre>
                </div>
              </div>
            </>
          )}

          {action.type === 'update_file' && (
            <>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">File Path:</div>
                <div className="p-2 bg-background-tertiary rounded border border-border">
                  <div className="text-sm font-mono">{action.path}</div>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">
                  Old Content ({action.oldContent?.length || 0} characters):
                </div>
                <div className="p-3 bg-background-tertiary rounded border border-border max-h-48 overflow-y-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap text-foreground-muted line-through">{action.oldContent}</pre>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground-secondary mb-1">
                  New Content ({action.newContent?.length || 0} characters):
                </div>
                <div className="p-3 bg-background-tertiary rounded border border-border max-h-48 overflow-y-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap text-green-400">{action.newContent}</pre>
                </div>
              </div>
            </>
          )}

          {action.type === 'delete_file' && (
            <>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">File Path:</div>
                <div className="p-2 bg-background-tertiary rounded border border-border">
                  <div className="text-sm font-mono text-red-400">{action.path}</div>
                </div>
              </div>
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                <p className="text-sm text-red-400">
                  ⚠️ This file will be permanently deleted. Make sure you have a backup if needed.
                </p>
              </div>
            </>
          )}

          {action.type === 'rename_file' && (
            <>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">Old Path:</div>
                <div className="p-2 bg-background-tertiary rounded border border-border">
                  <div className="text-sm font-mono text-foreground-muted line-through">{action.oldPath}</div>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-xs font-semibold text-foreground-secondary mb-1">New Path:</div>
                <div className="p-2 bg-background-tertiary rounded border border-border">
                  <div className="text-sm font-mono text-green-400">{action.newPath}</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="btn-secondary px-4 py-2"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 flex items-center gap-2 rounded ${getButtonClass()}`}
          >
            {getIcon()}
            {getButtonText()}
          </button>
        </div>

        {/* Safety Note */}
        <div className="mt-4 p-2 bg-background-tertiary rounded border border-border">
          <p className="text-xs text-foreground-muted">
            <strong>Note:</strong> {action.type === 'delete_file'
              ? 'Deleted files cannot be recovered unless you have a backup or version control.'
              : 'You can undo this change by manually reverting the file or using version control.'}
          </p>
        </div>
      </div>
    </div>
  )
}


